import {
  CalendarOutlined,
  FilterFilled,
  SearchOutlined,
} from '@ant-design/icons';
import type { InputRef } from 'antd';
import { Button, DatePicker, Input, Space } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateUtc } from '../Utils/date';

export const useColumnSearchPropsWithSearchField = () => {
  const { t } = useTranslation();
  const searchInput = useRef<InputRef>(null);
  const { getTextSearchFilter } = useOnFilter();

  // include filters, onFilter and filterIcon
  const getColumnSearchPropsWithSearchField = (
    placeHolderText: string,
    recordKey: string
  ) => ({
    filterDropdown: ({
      selectedKeys,
      setSelectedKeys,
      confirm,
      clearFilters,
    }: any) => (
      // stop keyboard events from propagating to the table and triggering sorting/filtering
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={placeHolderText}
          value={selectedKeys[0]}
          onChange={(e) => {
            setSelectedKeys(e.target.value ? [e.target.value] : []);
            confirm({ closeDropdown: false });
          }}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            {' '}
            {t('legDepComp.search')}
          </Button>

          <Button
            onClick={() => {
              clearFilters?.();
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            {t('legDepComp.reset')}
          </Button>
        </Space>
      </div>
    ),

    ...getTextSearchFilter(recordKey),

    filterIcon: (filtered: boolean) => (
      <SearchOutlined
        style={{ color: filtered ? '#1677ff' : undefined, fontSize: '15px' }}
      />
    ),

    // focus on the input when click on the search icon filter
    onFilterDropdownOpenChange: (visible: boolean) => {
      if (visible) {
        // wait 100 ms for opening dropdown and focus on the input
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  return { getColumnSearchPropsWithSearchField };
};

// with filters, onFilter function and filterIcon
export const useColumnFilters = () => {
  const getFilterProps = <T extends Record<string, any>>(
    data: any[] = [],
    filterKey: string,
    recordKey: string = filterKey
  ): Partial<ColumnType<T>> => {
    const filters = Array.from(new Set(data.map((item) => item[filterKey])))
      .filter(Boolean)
      .map((val) => ({
        text: String(val),
        value: String(val),
      }));

    return {
      filters,
      onFilter: (value, record) => record[recordKey] === value,
      filterIcon: (filtered: boolean) => (
        <FilterFilled
          style={{ fontSize: '15px', color: filtered ? '#1677ff' : undefined }}
        />
      ),
    };
  };

  return { getFilterProps };
};

export const useFilterIcon = () => {
  return {
    filterIcon: (filtered: boolean) => (
      <FilterFilled
        style={{ fontSize: '15px', color: filtered ? '#1677ff' : undefined }}
      />
    ),
  };
};

export const useOnFilter = () => {
  const getExactMatchFilter = (recordKey: string) => {
    return {
      onFilter: (value: any, record: any) => record[recordKey] === value,
    };
  };

  // recordKey is a property name from object
  const getTextSearchFilter = (recordKey: string) => {
    return {
      onFilter: (value: any, record: any) => {
        // if the field is empty or no contains data, do not throw an error
        if (!record[recordKey]) return false;

        return record[recordKey]
          .toString()
          .toLowerCase()
          .includes(value.toString().toLowerCase());
      },
    };
  };

  return { getExactMatchFilter, getTextSearchFilter };
};

export const useDateFilter = () => {
  const { t } = useTranslation();
  const { RangePicker } = DatePicker;

  const getDateColumnSearchProps = (recordKey: string) => ({
    filterDropdown: ({
      selectedKeys,
      setSelectedKeys,
      confirm,
      clearFilters,
    }: any) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <RangePicker
          value={selectedKeys[0]}
          onChange={(dates: any) => setSelectedKeys(dates ? [dates] : [])}
          style={{ marginBottom: 8, display: 'flex' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<CalendarOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            {t('legDepComp.search')}
          </Button>
          <Button
            onClick={() => {
              clearFilters?.();
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            {t('legDepComp.reset')}
          </Button>
        </Space>
      </div>
    ),

    onFilter: (value: any, record: any) => {
      if (!record.registrationDate || !value || !value[0] || !value[1])
        return false;

      const recordDateRaw = new Date(record[recordKey]);
      const recordDate = Date.UTC(
        recordDateRaw.getUTCFullYear(),
        recordDateRaw.getUTCMonth(),
        recordDateRaw.getUTCDate()
      );

      const startD = new Date(value[0]);
      const startDate = Date.UTC(
        startD.getFullYear(),
        startD.getMonth(),
        startD.getDate()
      );

      const endD = new Date(value[1]);
      const endDate = Date.UTC(
        endD.getFullYear(),
        endD.getMonth(),
        endD.getDate()
      );

      return recordDate >= startDate && recordDate <= endDate;
    },
    filterIcon: (filtered: boolean) => (
      <CalendarOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    render: (date: Date) => formatDateUtc(date),
  });

  return { getDateColumnSearchProps };
};
